/**
 * Painel "Registrar venda assinada" (comercial, `:registrar_venda`): o
 * contrato foi assinado e o PCP ainda não digitou o pedido na planilha. A
 * venda passa a ocupar lugar na fila da fábrica até o PCP ligá-la ao pedido.
 *
 * Só venda JÁ ASSINADA (decisão 8): para "e se eu vender?" existe a
 * simulação, que não grava nada. Vindo da simulação, o formulário já chega
 * com os ambientes e a data que foram simulados.
 */
import React, { useEffect, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import {
  MAX_AMBIENTES_DA_VENDA,
  TAMANHO_CLIENTE,
  TAMANHO_NOME_AMBIENTE,
  TAMANHO_OBSERVACAO,
  corpoDoRegistro,
  erroDoRegistro,
} from "../../utils/capacidade";
import { CLASSES_NA_TELA } from "./apresentacaoSimulacao";
import { formularioVazio } from "./apresentacaoVendas";
import { BotaoTexto, Regua } from "./styles";
import { CorpoPainel, Formulario, LinhaBotoes, Secao } from "./stylesSimulacao";
import { CampoTexto, ErroInline, ListaAmbientes } from "./stylesVendas";

const AMBIENTE_NOVO = Object.freeze({ nome: "", classe: "normal" });

function EditorDeAmbientes({ ambientes, onChange }) {
  const alterar = (indice, campo, valor) =>
    onChange(ambientes.map((a, i) => (i === indice ? { ...a, [campo]: valor } : a)));
  const remover = (indice) => onChange(ambientes.filter((_, i) => i !== indice));
  return (
    <>
      <ListaAmbientes>
        {ambientes.map((ambiente, i) => (
          // A posição é a identidade: o nome pode ficar em branco ou repetir.
          <li key={i}>
            <input
              type="text"
              aria-label={`Nome do ambiente ${i + 1}`}
              placeholder={`Ambiente ${i + 1}`}
              maxLength={TAMANHO_NOME_AMBIENTE}
              value={ambiente.nome}
              onChange={(e) => alterar(i, "nome", e.target.value)}
            />
            <select
              aria-label={`Carga do ambiente ${i + 1}`}
              value={ambiente.classe}
              onChange={(e) => alterar(i, "classe", e.target.value)}
            >
              {CLASSES_NA_TELA.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.rotulo}
                </option>
              ))}
            </select>
            <BotaoTexto
              type="button"
              aria-label={`Tirar o ambiente ${i + 1}`}
              disabled={ambientes.length <= 1}
              onClick={() => remover(i)}
              style={{ alignSelf: "center" }}
            >
              <FiX size={15} aria-hidden="true" />
            </BotaoTexto>
          </li>
        ))}
      </ListaAmbientes>
      {ambientes.length < MAX_AMBIENTES_DA_VENDA ? (
        <BotaoTexto type="button" onClick={() => onChange([...ambientes, { ...AMBIENTE_NOVO }])}>
          <FiPlus size={13} aria-hidden="true" /> Adicionar ambiente
        </BotaoTexto>
      ) : null}
    </>
  );
}

/**
 * `aberto`, `onFechar`; `formInicial` (de `formularioVazio` ou
 * `formularioDaSimulacao`); `onRegistrar(corpo)` → Promise (rejeita com a
 * mensagem do backend); `hoje` em ISO.
 */
export default function RegistrarVendaAssinada({ aberto, onFechar, formInicial, onRegistrar, hoje }) {
  const [form, setForm] = useState(() => formInicial || formularioVazio(hoje));
  const [erroServidor, setErroServidor] = useState(null);

  // Abriu de novo: começa do formulário pedido (em branco ou da simulação).
  useEffect(() => {
    if (!aberto) return;
    setForm(formInicial || formularioVazio(hoje));
    setErroServidor(null);
    // Só na abertura: depois disso quem manda é o que a pessoa digita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  const erro = erroDoRegistro(form, hoje);
  const alterar = (campo) => (e) => setForm((atual) => ({ ...atual, [campo]: e.target.value }));

  const registrar = async () => {
    setErroServidor(null);
    try {
      await onRegistrar(corpoDoRegistro(form));
      onFechar();
    } catch (falha) {
      setErroServidor(falha?.message || "Não foi possível registrar a venda. Tente de novo.");
      throw falha;
    }
  };

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Registrar venda assinada"
      subtitle="Contrato assinado que o PCP ainda não digitou na planilha."
      width="min(94vw, 560px)"
      ariaLabelledBy="registrar-venda-titulo"
      footer={
        <LinhaBotoes>
          <AsyncButton
            onClick={registrar}
            disabled={Boolean(erro)}
            successMessage="Venda registrada: ela já ocupa lugar na fila até o PCP ligar ao pedido."
            silentError
          >
            Registrar venda
          </AsyncButton>
          <BotaoTexto type="button" onClick={onFechar}>
            Voltar
          </BotaoTexto>
        </LinhaBotoes>
      }
    >
      <CorpoPainel>
        <Secao aria-label="Venda">
          <Formulario>
            <label style={{ flexBasis: "100%" }}>
              Cliente
              <input
                type="text"
                value={form.cliente}
                maxLength={TAMANHO_CLIENTE}
                onChange={alterar("cliente")}
                placeholder="Como vai aparecer na planilha"
              />
            </label>
            <label>
              Assinado em
              <input type="date" max={hoje} value={form.dataAssinatura} onChange={alterar("dataAssinatura")} />
            </label>
            <label>
              Entrega desejada
              <input
                type="date"
                min={form.dataAssinatura || undefined}
                value={form.entregaDesejada}
                onChange={alterar("entregaDesejada")}
              />
            </label>
          </Formulario>
          <Regua>
            Use o nome do cliente como o PCP vai digitar na planilha: é por ele que a página sugere o pedido certo
            na hora de ligar.
          </Regua>
        </Secao>

        <Secao aria-label="Ambientes">
          <h3>Ambientes ({form.ambientes.length})</h3>
          <EditorDeAmbientes
            ambientes={form.ambientes}
            onChange={(ambientes) => setForm((atual) => ({ ...atual, ambientes }))}
          />
          <Regua>
            Cada ambiente conta 1 na fila. Leve (banheiro, lavabo) vale meio e pesado vale dois na conta com as
            exceções marcadas pelo PCP. Nome em branco vira "Ambiente N".
          </Regua>
        </Secao>

        <Secao aria-label="Observação">
          <CampoTexto>
            Observação (opcional)
            <textarea
              rows={3}
              maxLength={TAMANHO_OBSERVACAO}
              value={form.observacao}
              onChange={alterar("observacao")}
            />
          </CampoTexto>
        </Secao>

        {erroServidor ? <ErroInline role="alert">{erroServidor}</ErroInline> : null}
        {!erroServidor && erro ? <Regua role="note">{erro}</Regua> : null}
      </CorpoPainel>
    </SlideOverPanel>
  );
}
